import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, FileText, LayoutDashboard, MapPin, Search, Tag, User } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { track } from "../analytics/tracker";
import "./GlobalSearch.css";

const PAGES = [
  { label: "Discover Artists", to: "/search", keywords: "browse find book search talent" },
  { label: "Events Gallery", to: "/events-gallery", keywords: "photos videos past events" },
  { label: "AI Event Planner", to: "/planner", keywords: "plan party wedding budget ai" },
  { label: "Blog", to: "/blog", keywords: "articles news tips" },
  { label: "Help Center", to: "/help", keywords: "faq support contact" },
  { label: "Why BookTalent", to: "/trust", keywords: "trust verified stats reviews" },
  { label: "Join as Artist", to: "/signup?role=artist", keywords: "signup register list artist", guestOnly: true },
  { label: "Sign In", to: "/login", keywords: "login account", guestOnly: true },
];

// Dashboard sections per role; each dashboard reads `?tab=`.
const DASHBOARD_SECTIONS = {
  artist: [
    ["overview", "Overview"], ["profile", "Profile"], ["questionnaire", "Questionnaire / Rider"],
    ["packages", "Packages"], ["addons", "Add-ons"], ["media", "Media"], ["calendar", "Availability calendar"],
    ["bookings", "Bookings"], ["insights", "Insights"], ["reviews", "Reviews"], ["boost", "Boost profile"],
    ["subscription", "Subscription"], ["kyc", "KYC verification"], ["tech_rider", "Tech rider"],
  ].map(([tab, label]) => ({ label, to: `/artist?tab=${tab}` })),
  customer: [
    ["overview", "Overview"], ["drafts", "Drafts"], ["bookings", "My bookings"],
    ["events", "My events"], ["reviews", "Reviews"], ["messages", "Messages"],
  ].map(([tab, label]) => ({ label, to: `/customer?tab=${tab}` })),
};

const DASHBOARD_HOME = { admin: "/admin", agency: "/agency", corporate: "/corporate", onboarding_manager: "/onboarding" };

const matches = (query, ...fields) => {
  const needle = query.trim().toLowerCase();
  return !needle || fields.some((field) => String(field || "").toLowerCase().includes(needle));
};

export default function GlobalSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [remote, setRemote] = useState({ artists: [], categories: [], cities: [] });
  const [loading, setLoading] = useState(false);
  const [blogs, setBlogs] = useState(null);
  const [active, setActive] = useState(0);

  // Ctrl/Cmd+K anywhere, or "/" when not typing in a field.
  useEffect(() => {
    const onKey = (event) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName) || event.target.isContentEditable;
      if ((event.key === "k" && (event.ctrlKey || event.metaKey)) || (event.key === "/" && !typing)) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setActive(0);
    requestAnimationFrame(() => inputRef.current?.focus());
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (blogs === null) {
      api.get("/blogs").then((r) => setBlogs(r.data || [])).catch(() => setBlogs([]));
    }
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live artist / category / city matches.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setRemote({ artists: [], categories: [], cities: [] }); setLoading(false); return undefined; }
    setLoading(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      api.get(`/search/suggestions?q=${encodeURIComponent(q)}`)
        .then((r) => { if (!cancelled) setRemote({ artists: [], categories: [], cities: [], ...r.data }); })
        .catch(() => { if (!cancelled) setRemote({ artists: [], categories: [], cities: [] }); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  const groups = useMemo(() => {
    const q = query.trim();
    const pages = PAGES.filter((p) => !(p.guestOnly && user) && matches(q, p.label, p.keywords));
    const sections = user
      ? (DASHBOARD_SECTIONS[user.role] || [{ label: "My dashboard", to: DASHBOARD_HOME[user.role] || "/" }])
        .filter((s) => matches(q, s.label, "dashboard"))
      : [];
    const posts = q.length >= 2 ? (blogs || []).filter((b) => matches(q, b.title, b.excerpt)).slice(0, 5) : [];
    return [
      { id: "artists", title: "Artists", icon: User, items: remote.artists.map((a) => ({ label: a.label, sub: a.sub, to: `/artist/${a.id}` })) },
      { id: "categories", title: "Categories", icon: Tag, items: remote.categories.map((c) => ({ label: c.label, sub: "Browse artists in this category", to: `/search?category=${encodeURIComponent(c.label)}&fresh_search=1` })) },
      { id: "cities", title: "Cities", icon: MapPin, items: remote.cities.map((c) => ({ label: c.label, sub: "Browse artists in this city", to: `/search?city=${encodeURIComponent(c.label)}&fresh_search=1` })) },
      { id: "sections", title: "Your dashboard", icon: LayoutDashboard, items: sections },
      { id: "pages", title: "Pages", icon: ArrowRight, items: pages },
      { id: "blogs", title: "Blog articles", icon: FileText, items: posts.map((b) => ({ label: b.title, to: `/blog/${b.slug}` })) },
    ].filter((group) => group.items.length);
  }, [query, remote, blogs, user]);

  const flat = useMemo(() => {
    const items = groups.flatMap((group) => group.items.map((item) => ({ ...item, group: group.id })));
    const q = query.trim();
    if (q) items.push({ label: `Search all artists for “${q}”`, to: `/search?q=${encodeURIComponent(q)}&fresh_search=1`, group: "all" });
    return items;
  }, [groups, query]);

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const close = () => { setOpen(false); setQuery(""); };
  const go = (item) => {
    if (!item) return;
    track("global_search_select", { group: item.group, query: query.trim() || undefined });
    close();
    navigate(item.to);
  };

  const onInputKey = (event) => {
    if (event.key === "ArrowDown") { event.preventDefault(); setActive((i) => Math.min(flat.length - 1, i + 1)); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (event.key === "Enter") { event.preventDefault(); go(flat[active]); }
    else if (event.key === "Escape") { event.preventDefault(); close(); }
  };

  let index = -1;
  const renderItem = (item, Icon) => {
    index += 1;
    const i = index;
    return (
      <li key={`${item.group}-${item.to}-${i}`} role="option" aria-selected={active === i}>
        <button
          type="button"
          data-index={i}
          className={`gsearch-item ${active === i ? "active" : ""}`}
          onMouseMove={() => setActive(i)}
          onClick={() => go(item)}
        >
          <span className="gsearch-item-icon" aria-hidden="true"><Icon size={16} /></span>
          <span className="gsearch-item-text">
            <span className="gsearch-item-label">{item.label}</span>
            {item.sub && <span className="gsearch-item-sub">{item.sub}</span>}
          </span>
        </button>
      </li>
    );
  };

  const q = query.trim();
  return (
    <>
      <button type="button" className="gsearch-trigger" onClick={() => setOpen(true)} aria-label="Search BookTalent" data-testid="global-search-open">
        <Search size={16} aria-hidden="true" />
        <span className="gsearch-trigger-label">Search</span>
        <kbd className="gsearch-trigger-kbd">Ctrl K</kbd>
      </button>

      {open && (
        <div className="gsearch-backdrop" onMouseDown={close} data-testid="global-search">
          <div className="gsearch-panel" role="dialog" aria-modal="true" aria-label="Search BookTalent" onMouseDown={(e) => e.stopPropagation()}>
            <div className="gsearch-input-row">
              <Search size={18} aria-hidden="true" />
              <input
                ref={inputRef}
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKey}
                placeholder="Search artists, categories, cities, pages…"
                aria-label="Search"
                aria-controls="gsearch-results"
                data-testid="global-search-input"
              />
              <button type="button" className="gsearch-esc" onClick={close}>Esc</button>
            </div>

            <ul className="gsearch-results" id="gsearch-results" role="listbox" ref={listRef}>
              {groups.map((group) => (
                <li key={group.id} className="gsearch-group">
                  <div className="gsearch-group-title">{group.title}</div>
                  <ul>{group.items.map((item) => renderItem({ ...item, group: group.id }, group.icon))}</ul>
                </li>
              ))}
              {q && (
                <li className="gsearch-group">
                  <ul>{renderItem(flat[flat.length - 1], Search)}</ul>
                </li>
              )}
              {q.length >= 2 && loading && <li className="gsearch-note">Searching artists…</li>}
              {q.length >= 2 && !loading && !groups.length && (
                <li className="gsearch-note">No direct matches. Try the full artist search above.</li>
              )}
            </ul>

            <div className="gsearch-footer">
              <span><kbd>↑</kbd><kbd>↓</kbd> to move</span>
              <span><kbd>Enter</kbd> to open</span>
              <span><kbd>Esc</kbd> to close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
